const TRUE_VALUES = new Set(['true', '1', 'yes']);
const FALSE_VALUES = new Set(['false', '0', 'no']);

export type RoleName = 'admin' | 'viewer';
export type BlockedActionMode = 'disable' | 'hide';

export interface RolePermissions {
  roles: string[];
  isAdmin: boolean;
  isViewer: boolean;
  /** True when the user has at least one Emeras-recognized role (`admin` or `viewer`). */
  hasValidRole: boolean;
  canWriteDenm: boolean;
  canWriteIvim: boolean;
  canManageRsu: boolean;
  canManagePmv: boolean;
  canManageSettings: boolean;
  blockedActionMode: BlockedActionMode;
}

interface RealmAccessClaim {
  roles?: unknown;
}

interface AuthProfileClaims {
  groups?: unknown;
  realmAccess?: RealmAccessClaim;
  'realm_access'?: RealmAccessClaim;
}

const normalizeRole = (role: string): string => role.trim().toLowerCase();

const parseRoleList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === 'string')
      .map(normalizeRole);
  }

  if (typeof value === 'string') {
    return [normalizeRole(value)];
  }

  return [];
};

const unique = (values: string[]): string[] => Array.from(new Set(values));

/** Base64url JWT segment → JSON source string (browser `atob`, Node `Buffer` fallback for scripts/tests). */
const decodeJwtSegmentToString = (segment: string): string => {
  const b64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const padded = b64 + pad;
  if (typeof globalThis.atob === 'function') {
    return globalThis.atob(padded);
  }
  return Buffer.from(padded, 'base64').toString('utf8');
};

/**
 * Parses JWT payload JSON without verifying the signature (caller already trusts the token from OIDC).
 * Used to read `realm_access` / `groups` from the **access token** when they are missing on `user.profile`
 * (Keycloak often puts realm roles only on the access token, not the ID token).
 */
export const decodeJwtPayloadClaims = (jwt?: string | null): AuthProfileClaims | undefined => {
  if (!jwt || typeof jwt !== 'string') return undefined;
  const parts = jwt.split('.');
  if (parts.length < 2) return undefined;
  try {
    const json = decodeJwtSegmentToString(parts[1]);
    const payload = JSON.parse(json) as unknown;
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return undefined;
    return payload as AuthProfileClaims;
  } catch {
    return undefined;
  }
};

export const getRolesFromProfile = (profile?: AuthProfileClaims): string[] => {
  if (!profile) return [];

  const groupsRoles = parseRoleList(profile.groups);
  if (groupsRoles.length > 0) {
    return unique(groupsRoles);
  }

  const { realm_access: realmAccessFallback } = profile;
  const realmAccessClaim = profile.realmAccess ?? realmAccessFallback;
  const realmRoles = parseRoleList(realmAccessClaim?.roles);
  return unique(realmRoles);
};

export interface OidcUserLike {
  profile?: AuthProfileClaims;
  /** OIDC access JWT (same value as `User.access_token` from oidc-client-ts). */
  accessToken?: string;
}

/**
 * Resolves roles for RBAC from OIDC `user`: first from `profile` (ID token / userinfo), then — if that
 * yields no roles — from the **access token** payload (where Keycloak usually exposes `realm_access.roles`).
 */
export const getRolesFromOidcUser = (user?: OidcUserLike | null): string[] => {
  const fromProfile = getRolesFromProfile(user?.profile);
  if (fromProfile.length > 0) {
    return fromProfile;
  }
  return getRolesFromProfile(decodeJwtPayloadClaims(user?.accessToken));
};

export const getBlockedActionMode = (): BlockedActionMode => {
  const rawValue = process.env.RBAC_HIDE_BLOCKED_ACTIONS;
  if (!rawValue) return 'disable';
  return TRUE_VALUES.has(rawValue.toLowerCase()) ? 'hide' : 'disable';
};

/**
 * When enabled, every user is treated as `admin` regardless of OIDC roles (temporary until IdP roles are wired).
 * **Default: enabled** if the env var is unset. Set `RBAC_TEMP_DEFAULT_ADMIN=false` (or `0` / `no`) for strict RBAC.
 */
export const getTempDefaultAdminFallbackEnabled = (): boolean => {
  const raw = process.env.RBAC_TEMP_DEFAULT_ADMIN?.trim();
  if (!raw) return true;
  if (FALSE_VALUES.has(raw.toLowerCase())) return false;
  return true;
};

export const buildRolePermissions = (roles: string[]): RolePermissions => {
  const effectiveRoles = getTempDefaultAdminFallbackEnabled() ? ['admin'] : roles;
  const normalizedRoles = unique(effectiveRoles.map(normalizeRole));
  const isAdmin = normalizedRoles.includes('admin');
  const isViewer = normalizedRoles.includes('viewer');
  const hasValidRole = isAdmin || isViewer;
  const canWrite = isAdmin;

  return {
    roles: normalizedRoles,
    isAdmin,
    isViewer,
    hasValidRole,
    canWriteDenm: canWrite,
    canWriteIvim: canWrite,
    canManageRsu: canWrite,
    canManagePmv: canWrite,
    canManageSettings: canWrite,
    blockedActionMode: getBlockedActionMode(),
  };
};
